import * as DataCatalogShared from '@tableau/catalog-ui-temp';
import * as DataCatalogClientComponent from '@tableau/catalog-ui-temp';
import * as MetricsEngine from '@tableau/metrics-engine';
import { microviewHostComponentFactory } from '@tableau/microview/lib/src/MicroviewHostComponentFactory';
import { MicroviewSuperHostService } from '@tableau/microview/lib/src/MicroviewHostService';
import { omniboxMetadata } from '@tableau/omnibox-common/lib/src/Metadata';
import * as SharedUrls from '@tableau/shared-urls';
import * as SharedWidgets from '@tableau/widgets';
import * as DataGridWidget from '@tableau/widgets-datagrid';
import * as MicroviewGraphql from 'graphql';
import moment from 'moment';
import React from 'react';
import * as ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import * as ReactRouter from 'react-router';
import * as ReactRouterDOM from 'react-router-dom';
import { style } from 'typestyle';

import { LocalizationLoader } from '~src/commonInfra/services/LocalizationLoader';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { MapStateToProps } from '~src/commonInfra/utils/ConnectUtils';
import { getSessionManagementServiceInstance, SessionManagementService } from '~src/sessionManagement/SessionManagementService';
import { ServerSettingsUnauthenticated } from '~src/settings/services/ServerSettingsUnauthenticated';

import { SessionSiteSelectors, SessionUserSelectors } from '../../bootstrap/redux/selectors/SessionInfoSelectors';
import { AppState } from '../../bootstrap/redux/state/AppState';
import * as OmniboxSpecificListener from '../communicationChannelListeners/OmniboxSpecificListener';

// It's a temp solution to put the shared dependency exporting in Omnibox folder.
// It will be moved to a common microview folder later.
// eslint-disable-next-line quotes
(window as any)["MicroviewSharedWidgets"] = SharedWidgets;
(window as any)['MicroviewReact'] = React;
(window as any)['MicroviewReactDOM'] = ReactDOM;
(window as any)['MicroviewDataCatalogClientComponent'] =
    DataCatalogClientComponent;
(window as any)['MicroviewDataCatalogShared'] = DataCatalogShared;
(window as any)['MicroviewReactRouter'] = ReactRouter;
(window as any)['MicroviewReactRouterDOM'] = ReactRouterDOM;
(window as any)['MicroviewSharedUrls'] = SharedUrls;
(window as any)['MicroviewMetricsEngine'] = MetricsEngine;
(window as any)['MicroviewDataGridWidget'] = DataGridWidget;
(window as any)['MicroviewMoment'] = moment;
(window as any)['MicroviewGraphql'] = MicroviewGraphql;

export type OmniboxMicroViewOwnProps = {
    /*
     * This prop is used for integration with VizViewerPageHeader. That component has a toggle button to show/hide omnibox.
     * If the omnibox is closed by the toggle button, the focus should remain in the toggle button.
     * Or if the omnibox is closed by pressing ESC in it, the focus should remain in the toggle button.
     * Omnibox use this prop to communicate with VizViewerPageHeader.
    */
    closedWithFocus: () => void;
    /*
     * This prop is used for integration with VizViewerPageHeader. That component has a toggle button to show/hide omnibox.
     * If the omnibox is closed because the whole JavaScript window loses focus e.g. the user is in a Viz viewer page and searches something to pop up the
     * search result, then the user clicks the viz which is inside an iFrame.
     * In this case, the whole omnibox should be close and the toggle button should not hold the focus.
     * Omnibox use this prop to communicate with VizViewerPageHeader.
    */
    closedWithoutFocus: () => void;
    shouldOpenWithFocusInSearchTextbox: boolean;
    isSearchOpen?: boolean;
    isSearchInputBoxActive: (isActive: boolean) => void;
    inSearchResultPage?: boolean;
    searchTerm?: string;
    onSearchTermChanged?: (searchTerm: string) => void;
    onFocus?: () => void;
};

export type OmniboxStateProps = {
    siteRole: string;
    locale: string;
}

export type OmniboxMicroViewHostProps = OmniboxMicroViewOwnProps & OmniboxStateProps;

export const ErrorUITestId = 'OmniboxMicroViewHostError';

const errorStyles = style({
    fontSize: '14px',
    textAlign: 'center',
    lineHeight: '1.5',
    position: 'absolute',
    left: '50%',
    top: '40%',
    transform: 'translate(-50%,-50%)',
    $nest: {
        '.title': {
            fontSize: '18px',
            marginBottom: '16px',
            lineHeight: '1.5',
            color: CommonStyles.Colors.Grey51,
        },
        '.sub-title': {
            fontSize: '14px',
            marginBottom: '16px',
            color: CommonStyles.Colors.Grey77,
        }
    },
});

function getLocale(): string {
    const sessionManagementService: SessionManagementService = getSessionManagementServiceInstance();
    const language = sessionManagementService.sessionLanguage() ?? ServerSettingsUnauthenticated.serverLanguage();
    const localeResult = LocalizationLoader.selectLocaleOrDefault(language);
    return localeResult;
}

function listenToSpecificCommunicationChannels() {
    OmniboxSpecificListener.startListening();
}

MicroviewSuperHostService.instance.bootstrapMicroview(omniboxMetadata, getLocale, listenToSpecificCommunicationChannels);

export const OmniboxMicroViewHost = microviewHostComponentFactory<OmniboxMicroViewHostProps>(omniboxMetadata);

const mapStateToProps: MapStateToProps<OmniboxStateProps> = (state: AppState) => ({
    siteRole: SessionSiteSelectors.siteRole(state),
    locale: SessionUserSelectors.locale(state)
});


export const ConnectedOmniboxMicroViewHost: React.ComponentClass<OmniboxMicroViewOwnProps> = connect(mapStateToProps)(OmniboxMicroViewHost);
